import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { authAPI, workspaceAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Users,
  Trash2,
  Pencil,
  UserPlus,
  Loader2,
  Crown
} from 'lucide-react';

const PERMISSION_STRUCTURE = [
  {
    id: 'full_access',
    label: 'Full Access',
    description: 'Complete access to all features',
    permissions: ['full_access']
  },
  {
    id: 'seo_level',
    label: 'SEO-Level',
    description: 'Create and manage SEO briefs',
    permissions: ['create_brief', 'edit_brief', 'approve_brief'],
    children: [
      { id: 'create_brief', label: 'Create Brief' },
      { id: 'edit_brief', label: 'Edit Brief' },
      { id: 'approve_brief', label: 'Approve Brief' }
    ]
  },
  {
    id: 'writer_level',
    label: 'Writer-Level',
    description: 'View and edit assigned content',
    permissions: ['view_content', 'edit_content', 'submit_for_review'],
    children: [
      { id: 'view_content', label: 'View Content' },
      { id: 'edit_content', label: 'Edit Content' },
      { id: 'submit_for_review', label: 'Submit for Review' }
    ]
  },
  {
    id: 'review_level',
    label: 'Review-Level',
    description: 'Review and approve content',
    permissions: ['comment', 'request_revision', 'approve_content'],
    children: [
      { id: 'comment', label: 'Comment' },
      { id: 'request_revision', label: 'Request Revision' },
      { id: 'approve_content', label: 'Approve Content' }
    ]
  }
];

export default function WorkspaceSettings() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [workspace, setWorkspace] = useState(null);
  const [members, setMembers] = useState([]);
  const [user, setUser] = useState(null);
  
  const [showInvite, setShowInvite] = useState(false);
  const [showEditMember, setShowEditMember] = useState(null);
  const [showRemoveMember, setShowRemoveMember] = useState(null);
  
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('');
  const [invitePermissions, setInvitePermissions] = useState([]);
  const [editPermissions, setEditPermissions] = useState([]);
  const [editRole, setEditRole] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [userRes, wsRes, membersRes] = await Promise.all([
        authAPI.getMe(),
        workspaceAPI.get(workspaceId),
        workspaceAPI.getMembers(workspaceId)
      ]);
      
      setUser(userRes.data);
      setWorkspace(wsRes.data);
      setMembers(membersRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      } else {
        toast.error('Failed to load workspace');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [workspaceId, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const hasFullAccess = workspace?.permissions?.includes('full_access');

  const togglePermission = (permissionId, currentPermissions, setPermissions) => {
    const category = PERMISSION_STRUCTURE.find(c => c.id === permissionId);
    
    if (category) {
      const isCurrentlySelected = category.permissions.every(p => currentPermissions.includes(p));
      
      if (isCurrentlySelected) {
        setPermissions(currentPermissions.filter(p => !category.permissions.includes(p)));
      } else {
        const newPermissions = [...new Set([...currentPermissions, ...category.permissions])];
        if (permissionId === 'full_access') {
          setPermissions(['full_access']);
        } else {
          setPermissions(newPermissions.filter(p => p !== 'full_access'));
        }
      }
    } else {
      if (currentPermissions.includes(permissionId)) {
        setPermissions(currentPermissions.filter(p => p !== permissionId));
      } else {
        const newPermissions = [...currentPermissions.filter(p => p !== 'full_access'), permissionId];
        setPermissions(newPermissions);
      }
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim() || !inviteRole.trim()) {
      toast.error('Please enter email and role');
      return;
    }
    if (invitePermissions.length === 0) {
      toast.error('Please select at least one permission');
      return;
    }
    
    try {
      const res = await workspaceAPI.addMember(workspaceId, {
        email: inviteEmail,
        role_name: inviteRole,
        permissions: invitePermissions
      });
      
      setMembers([...members, res.data]);
      setInviteEmail('');
      setInviteRole('');
      setInvitePermissions([]);
      setShowInvite(false);
      toast.success('Member invited');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite member');
    }
  };

  const handleUpdateMember = async () => {
    if (!showEditMember) return;
    
    try {
      const res = await workspaceAPI.updateMember(workspaceId, showEditMember.member_id, {
        role_name: editRole,
        permissions: editPermissions
      });
      
      setMembers(members.map(m => m.member_id === showEditMember.member_id ? res.data : m));
      setShowEditMember(null);
      toast.success('Member updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update member');
    }
  };

  const handleRemoveMember = async () => {
    if (!showRemoveMember) return;
    
    try {
      await workspaceAPI.removeMember(workspaceId, showRemoveMember);
      setMembers(members.filter(m => m.member_id !== showRemoveMember));
      setShowRemoveMember(null);
      toast.success('Member removed');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const openEditMember = (member) => {
    setShowEditMember(member);
    setEditRole(member.role_name);
    setEditPermissions(member.permissions || []);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-white border-b border-border">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-semibold text-lg">{workspace?.name}</h1>
              <p className="text-sm text-muted-foreground">Workspace Settings</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-lg border border-border">
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-primary" />
              <h2 className="font-semibold">Team Members</h2>
              <span className="text-sm text-muted-foreground">({members.length}/4)</span>
            </div>
            {hasFullAccess && (
              <Button onClick={() => setShowInvite(true)} size="sm">
                <UserPlus className="w-4 h-4 mr-2" />
                Invite Member
              </Button>
            )}
          </div>
          
          <div className="divide-y divide-border">
            {members.map(member => (
              <div key={member.member_id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="text-sm font-medium text-primary">
                      {member.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{member.name}</span>
                      {member.is_owner && (
                        <Crown className="w-4 h-4 text-amber-500" />
                      )}
                      {member.pending && (
                        <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Pending</span>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">{member.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">{member.role_name}</span>
                      <span className="text-xs text-muted-foreground">
                        {member.permissions?.includes('full_access') ? 'Full Access' : `${member.permissions?.length || 0} permissions`}
                      </span>
                    </div>
                  </div>
                </div>
                
                {hasFullAccess && !member.is_owner && (
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEditMember(member)}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setShowRemoveMember(member.member_id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Invite Member Dialog - Responsive & Scrollable */}
      <Dialog open={showInvite} onOpenChange={setShowInvite}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Invite Team Member</DialogTitle>
            <DialogDescription>Add a new member to your workspace</DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            <div>
              <Label htmlFor="invite-email">Email Address</Label>
              <Input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="member@example.com"
                className="mt-1.5"
              />
            </div>
            
            <div>
              <Label htmlFor="invite-role">Role</Label>
              <Input
                id="invite-role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value)}
                placeholder="e.g., Writer, SEO Specialist"
                className="mt-1.5"
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Access Permissions</Label>
              <div className="space-y-3">
                {PERMISSION_STRUCTURE.map(category => {
                  const isFullAccess = category.id === 'full_access';
                  const categorySelected = isFullAccess
                    ? invitePermissions.includes('full_access')
                    : category.permissions.every(p => invitePermissions.includes(p));
                  
                  return (
                    <div key={category.id} className="border border-border rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <Checkbox
                          id={`invite-${category.id}`}
                          checked={categorySelected}
                          onCheckedChange={() => togglePermission(category.id, invitePermissions, setInvitePermissions)}
                        />
                        <div className="flex-1 min-w-0">
                          <label htmlFor={`invite-${category.id}`} className="font-medium text-sm cursor-pointer">
                            {category.label}
                          </label>
                          <p className="text-xs text-muted-foreground">{category.description}</p>
                          
                          {category.children && !invitePermissions.includes('full_access') && (
                            <div className="mt-2 pl-3 space-y-1 border-l-2 border-border">
                              {category.children.map(child => (
                                <div key={child.id} className="flex items-center gap-2">
                                  <Checkbox
                                    id={`invite-${child.id}`}
                                    checked={invitePermissions.includes(child.id)}
                                    onCheckedChange={() => togglePermission(child.id, invitePermissions, setInvitePermissions)}
                                  />
                                  <label htmlFor={`invite-${child.id}`} className="text-xs cursor-pointer">{child.label}</label>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          <div className="flex-shrink-0 pt-4 border-t">
            <Button onClick={handleInviteMember} className="w-full">
              Add Member
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Member Dialog */}
      <Dialog open={!!showEditMember} onOpenChange={() => setShowEditMember(null)}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle>Edit Member Permissions</DialogTitle>
            <DialogDescription>Update permissions for {showEditMember?.name}</DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto space-y-4 pr-2">
            <div>
              <Label htmlFor="edit-role">Role</Label>
              <Input
                id="edit-role"
                value={editRole}
                onChange={(e) => setEditRole(e.target.value)}
                className="mt-1.5"
              />
            </div>
            
            <div>
              <Label className="mb-2 block">Access Permissions</Label>
              <div className="space-y-3">
                {PERMISSION_STRUCTURE.map(category => {
                  const isFullAccess = category.id === 'full_access';
                  const categorySelected = isFullAccess
                    ? editPermissions.includes('full_access')
                    : category.permissions.every(p => editPermissions.includes(p));
                  
                  return (
                    <div key={category.id} className="border border-border rounded-lg p-3">
                      <div className="flex items-start gap-2">
                        <Checkbox
                          id={`edit-${category.id}`}
                          checked={categorySelected}
                          onCheckedChange={() => togglePermission(category.id, editPermissions, setEditPermissions)}
                        />
                        <div className="flex-1 min-w-0">
                          <label htmlFor={`edit-${category.id}`} className="font-medium text-sm cursor-pointer">
                            {category.label}
                          </label>
                          <p className="text-xs text-muted-foreground">{category.description}</p>
                          
                          {category.children && !editPermissions.includes('full_access') && (
                            <div className="mt-2 pl-3 space-y-1 border-l-2 border-border">
                              {category.children.map(child => (
                                <div key={child.id} className="flex items-center gap-2">
                                  <Checkbox
                                    id={`edit-${child.id}`}
                                    checked={editPermissions.includes(child.id)}
                                    onCheckedChange={() => togglePermission(child.id, editPermissions, setEditPermissions)}
                                  />
                                  <label htmlFor={`edit-${child.id}`} className="text-xs cursor-pointer">{child.label}</label>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          <div className="flex-shrink-0 pt-4 border-t">
            <Button onClick={handleUpdateMember} className="w-full">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Remove Member Confirmation */}
      <AlertDialog open={!!showRemoveMember} onOpenChange={() => setShowRemoveMember(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Member</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this member? They will lose access to all workspace content.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveMember} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
